import { useContext, useMemo } from "react";
import { DataTable, type ColumnDef, type ItemProps, ImperativeDialogContext } from "@repo/ui";
import { cn } from "@repo/utils";
import { workflowStatusMap } from "~/pages/administration/workflows-page/workflows-constants";
import type { WorkflowState } from "~/types/api";
import type { DropdownActionContext } from "~/types/common";
import { ListFilters } from "./filters/list-filters";
import { useListData } from "./hooks/use-list-data";
import type { ListDTO } from "./list.types";
import { ListDialog } from "./lists-dialog/list-dialog";

export type ListActionsContext = DropdownActionContext<ListDTO>;

export const Lists = () => {
  const { lists, isLoading, deleteList } = useListData();
  const dialogContext = useContext(ImperativeDialogContext);
  const { showDialog, closeDialog } = dialogContext ?? {};

  const columns: ColumnDef<ListDTO>[] = [
    {
      key: "name",
      header: "List Name",
      width: "flex-1",
      searchable: true,
    },
    {
      key: "customer",
      header: "Customer",
      width: "flex-1",
      searchable: true,
      render: (_, list) => list.customer?.name ?? "-",
      searchValue: (list) => list.customer?.name ?? "",
    },
    {
      key: "workflow",
      header: "Workflow Package",
      width: "flex-1",
      searchable: true,
      render: (_, list) => list.workflow?.name ?? "-",
      searchValue: (list) => list.workflow?.name ?? "",
    },
    {
      key: ("workflow" + "State") as keyof ListDTO,
      header: "Workflow Status",
      width: "flex-1",
      searchable: true,
      render: (_, list) => (
        <span
          className={cn(
            "rounded-full px-3 py-1 text-xs font-medium",
            list.workflow?.state === "Active"
              ? "bg-green-100 text-green-700"
              : "bg-gray-100 text-gray-700"
          )}
        >
          {list.workflow?.state ? workflowStatusMap[list.workflow.state as WorkflowState] : "-"}
        </span>
      ),
      searchValue: (list) => list.workflow?.state ?? "",
    },
    {
      key: ("workflow" + "Version") as keyof ListDTO,
      header: "Workflow Version",
      width: "flex-1",
      searchable: true,
      render: (_, list) =>
        list.workflow?.version ? `v. ${String(list.workflow.version).padStart(2, "0")}` : "-",
      searchValue: (list) => list.workflow?.version?.toString() ?? "",
    },
  ];

  const handleEditList = (list: ListDTO) => {
    showDialog?.({
      content: <ListDialog mode="edit" list={list} onClose={closeDialog} onSuccess={closeDialog} />,
    });
  };

  const showDeleteConfirmation = (list: ListDTO) => {
    showDialog?.({
      title: "Delete List",
      content: (
        <div className="w-full">
          <div className="bg-gxe-secondary flex justify-center rounded-md p-2">
            You are about to delete list by {list.customer?.name}:
            <span className="text-gxe-primary ml-2 font-bold">{list.name}</span>
          </div>
        </div>
      ),
      confirmText: "Delete",
      cancelText: "Cancel",
      cancelVariant: "secondary",
      confirmVariant: "primary",
      onConfirm: () => {
        if (list.workflow?.id) {
          deleteList(list.id, list.workflow.id, { onSuccess: closeDialog });
        }
      },
    });
  };

  const actions: ItemProps<ListActionsContext>[] = [
    {
      id: "edit-list",
      label: "Edit List",
      onClick: (ctx: ListActionsContext) => handleEditList(ctx.row),
    },
    {
      id: "delete-list",
      label: "Delete List",
      onClick: (ctx: ListActionsContext) => showDeleteConfirmation(ctx.row),
    },
  ];

  const listNameOptions = useMemo(
    () =>
      Array.from(new Set(lists?.map((list) => list.name)))
        .sort()
        .map((name) => ({ label: name, value: name })),
    [lists]
  );

  const customerOptions = useMemo(
    () =>
      Array.from(
        new Map(
          (lists ?? [])
            .filter((l) => l.customer?.id) // Filter out missing customers
            .map((l) => [l.customer.id, { label: l.customer.name, value: l.customer.id }])
        ).values()
      ),
    [lists]
  );

  const workflowOptions = useMemo(
    () =>
      Array.from(
        new Map(
          (lists ?? [])
            .filter((l) => l.workflow?.id) // Filter out missing workflows
            .map((l) => [l.workflow.id, { label: l.workflow.name, value: l.workflow.id }])
        ).values()
      ),
    [lists]
  );

  return (
    <DataTable<ListDTO>
      data={lists ?? []}
      columns={columns}
      actions={actions}
      isLoading={isLoading}
      renderColumnFilters={
        (lists?.length ?? 0) > 0
          ? (_, setColumnFilter, filters) => (
              <ListFilters<ListDTO>
                setColumnFilter={setColumnFilter}
                filters={filters}
                listNameOptions={listNameOptions}
                customerOptions={customerOptions}
                workflowOptions={workflowOptions}
              />
            )
          : undefined
      }
    />
  );
};
